// src/pages/Admin/AdminTagBatches.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  Badge,
} from "react-bootstrap";
import { Auth } from "aws-amplify";
import AdminNav from "../../components/AdminNav";
import "./AdminTagBatches.css";

const API_BASE = process.env.REACT_APP_API_BASE || "";

function nextBatchIdFromHistory(items) {
  let max = 0;
  (items || []).forEach((it) => {
    const m = String(it.batchId || "").match(/BATCH-(\d+)/i);
    if (m) max = Math.max(max, Number(m[1]) || 0);
  });
  const next = String(max + 1).padStart(3, "0");
  return `BATCH-${next}`;
}

export default function AdminTagBatches() {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resending, setResending] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [items, setItems] = useState([]);

  // Form state
  const [start, setStart] = useState(1111);
  const [count, setCount] = useState(200);
  const [numBlack, setNumBlack] = useState(100);
  const [numWhite, setNumWhite] = useState(100);
  const [batchId, setBatchId] = useState("BATCH-001");
  const [includeCodeInUrl, setIncludeCodeInUrl] = useState(true);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");

  const computedEnd = useMemo(() => {
    const s = Number(start) || 0;
    const c = Number(count) || 0;
    return s && c ? s + c - 1 : "";
  }, [start, count]);

  const splitOk = useMemo(
    () => Number(count) === Number(numBlack) + Number(numWhite),
    [count, numBlack, numWhite]
  );

  async function api(path, options = {}) {
    if (!API_BASE) throw new Error("Missing REACT_APP_API_BASE.");

    const session = await Auth.currentSession();
    const token = session.getAccessToken().getJwtToken();

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`${res.status} ${txt}`);
    }
    return res.json().catch(() => ({}));
  }

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await api("/admin/tag-batches", { method: "GET" });
      const list = data.items || [];
      setItems(list);

      const suggested = nextBatchIdFromHistory(list);
      setBatchId((prev) => (prev && prev !== "BATCH-001" ? prev : suggested));
    } catch (e) {
      setError("Could not load tag batches.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!splitOk) {
      setError("Count must equal numBlack + numWhite.");
      return;
    }
    if (!batchId) {
      setError("Batch ID is required.");
      return;
    }

    setCreating(true);
    try {
      const ccArr = emailCc
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = {
        start: Number(start),
        count: Number(count),
        numBlack: Number(numBlack),
        numWhite: Number(numWhite),
        batchId: String(batchId).trim(),
        includeCodeInUrl,
        emailTo: String(emailTo).trim(),
        emailCc: ccArr,
      };

      const result = await api("/admin/tag-batches", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setSuccess(
        `Batch ${result.batchId} generated and emailed to ${result.email?.to || "vendor"}.`
      );
      await load();
    } catch (e) {
      const msg = String(e.message || "");
      if (msg.includes("409")) setError("That batchId already exists. Pick a new one.");
      else setError("Failed to generate batch. Check inputs and try again.");
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const handleResend = async (batchIdToResend) => {
    setError("");
    setSuccess("");
    setResending(batchIdToResend);

    try {
      const ccArr = emailCc
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const result = await api(
        `/admin/tag-batches/${encodeURIComponent(batchIdToResend)}/resend`,
        {
          method: "POST",
          body: JSON.stringify({
            emailTo: String(emailTo).trim(),
            emailCc: ccArr,
          }),
        }
      );

      setSuccess(`Resent ${result.batchId} download link to ${result.emailTo}.`);
      await load();
    } catch (e) {
      setError("Failed to resend email.");
      console.error(e);
    } finally {
      setResending("");
    }
  };

  const statusBadge = (s) => {
    const v = String(s || "unknown").toLowerCase();
    if (v === "complete") return <Badge bg="success">complete</Badge>;
    if (v === "running") return <Badge bg="warning" text="dark">running</Badge>;
    if (v === "failed") return <Badge bg="danger">failed</Badge>;
    return <Badge bg="secondary">{v}</Badge>;
  };

  return (
    <div className="admin-page-wrapper">
      <Container className="admin-page-shell">
        <AdminNav />

        <div className="admin-page-header">
          <div className="admin-page-header-left">
            <div className="admin-page-header-icon">🧩</div>
            <div>
              <h3 className="admin-page-title">Tag Batches</h3>
              <div className="admin-page-subtitle">
                Generate vendor QR packages (CSV + SVG ZIP) and email a download link.
              </div>
            </div>
          </div>

          <div className="admin-page-header-right">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={load}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </div>

        <div className="admin-page-body">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          <Card className="admin-card mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Create new batch</h5>
                <div className="text-muted small">
                  End auto: <strong>{computedEnd || "-"}</strong>
                </div>
              </div>

              <Form onSubmit={handleCreate}>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Batch ID</Form.Label>
                      <Form.Control
                        value={batchId}
                        onChange={(e) => setBatchId(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Start</Form.Label>
                      <Form.Control
                        type="number"
                        value={start}
                        onChange={(e) => setStart(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Count</Form.Label>
                      <Form.Control
                        type="number"
                        value={count}
                        onChange={(e) => setCount(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Include code in URL</Form.Label>
                      <Form.Check
                        type="switch"
                        id="includeCodeInUrl"
                        checked={includeCodeInUrl}
                        onChange={(e) => setIncludeCodeInUrl(e.target.checked)}
                        label={includeCodeInUrl ? "Yes" : "No"}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label># Black</Form.Label>
                      <Form.Control
                        type="number"
                        value={numBlack}
                        onChange={(e) => setNumBlack(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label># White</Form.Label>
                      <Form.Control
                        type="number"
                        value={numWhite}
                        onChange={(e) => setNumWhite(e.target.value)}
                      />
                      {!splitOk && (
                        <div className="small text-danger mt-1">
                          Count must equal black + white.
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Email To (vendor/test)</Form.Label>
                      <Form.Control
                        value={emailTo}
                        onChange={(e) => setEmailTo(e.target.value)}
                        placeholder="you@email.com"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Email CC (comma separated)</Form.Label>
                      <Form.Control
                        value={emailCc}
                        onChange={(e) => setEmailCc(e.target.value)}
                        placeholder="ops@..., you@..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end mt-3">
                  <Button
                    type="submit"
                    className="lpp-pill-btn"
                    disabled={creating || !splitOk}
                  >
                    {creating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />{" "}
                        Generating…
                      </>
                    ) : (
                      "Generate + Email"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="admin-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">History</h5>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={load}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />{" "}
                  Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="text-muted">No batches yet.</div>
              ) : (
                <Table className="admin-table mb-0" responsive hover>
                  <thead>
                    <tr>
                      <th>Batch</th>
                      <th>Status</th>
                      <th>Range</th>
                      <th>Counts</th>
                      <th>Last emailed</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.batchId}>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <strong>{it.batchId}</strong>
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>{statusBadge(it.status)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          LP{it.start}–LP{it.end} ({it.count})
                        </td>
                        <td className="text-muted">
                          processed: {it.tagsProcessed || 0} · codes: {it.codesCreated || 0} ·
                          svgs: {it.svgsGenerated || 0}
                        </td>
                        <td className="text-muted" style={{ whiteSpace: "nowrap" }}>
                          {it.lastEmailedAt
                            ? new Date(it.lastEmailedAt).toLocaleString()
                            : "-"}
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={resending === it.batchId}
                            onClick={() => handleResend(it.batchId)}
                          >
                            {resending === it.batchId ? "Resending…" : "Resend link"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
}
